var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import cc from 'classcat';
import moment from 'moment';
import Calendar from '../../../components/Calendar';
import SearchField from '../../../components/SearchField';
import IconButton from '../../../components/IconButton';
import CriteriaList from '../../../components/CriteriaList';
import FilterButtons from '../../../components/FilterButtons';
import ArrowBackIcon from '../../../assets/icons/arrow-back.svg';
import filterBySearchQuery from './filterBySearchQuery';
import { useSearchCriteria } from '../../../hooks/useSearchCriteria';
import updateSelectedCriteria from '../../../store/appGlobalData/actions/updateSelectedCriteria';
import useAppGlobalData from '../../../hooks/useAppGlobalData';
import { dateToUrlDate, stringToMoment } from '../../../shared/functions';
import routesPaths from '../../../routes/routesPaths';
import updateSelectedDate from '../../../store/appGlobalData/actions/updateSelectedDate';
import './header.style.scss';
/**
 * The application main header
 */
var Header = function (_a) {
    var detailParams = _a.detailParams, dailyParams = _a.dailyParams, monthlyParams = _a.monthlyParams, className = _a.className, rest = __rest(_a, ["detailParams", "dailyParams", "monthlyParams", "className"]);
    var _b = useSearchCriteria(), searchCriteria = _b[0], fetchSearchCriteria = _b[1].fetchSearchCriteria;
    var _c = useAppGlobalData(), selectedCriteria = _c.selectedCriteria, selectedNewsGroup = _c.selectedNewsGroup, resetSearchCriteria = _c.resetSearchCriteria, onSelectedNewsGroupChange = _c.onSelectedNewsGroupChange;
    var list = useRef(null);
    var location = useLocation();
    var history = useHistory();
    var dispatch = useDispatch();
    var currentDate = (dailyParams === null || dailyParams === void 0 ? void 0 : dailyParams.date) || (detailParams === null || detailParams === void 0 ? void 0 : detailParams.date);
    var _d = useState(''), searchQuery = _d[0], setSearchQuery = _d[1];
    var _e = useState(false), showCriteriaList = _e[0], setShowCriteriaList = _e[1];
    var _f = useState(stringToMoment(currentDate).isValid() ? stringToMoment(currentDate) : moment()), selectedDate = _f[0], setSelectedDate = _f[1];
    var showBackBtn = !!selectedCriteria;
    var availableCriteria = useMemo(function () { return filterBySearchQuery(searchQuery, searchCriteria.data); }, [filterBySearchQuery, searchQuery, searchCriteria]);
    var isApp = useMemo(function () {
        var query = new URLSearchParams(location.search);
        var appParam = query.get('sport24app');
        return appParam === 'true';
    }, [location]);
    // Add a listener to close criteria list on outside click
    useEffect(function () {
        document.addEventListener('mouseup', function (event) {
            if (list.current && !list.current.contains(event.target)) {
                setShowCriteriaList(false);
            }
        });
    }, [list]);
    // When the component mounts, fetch the searchCriteria data.
    useEffect(function () { fetchSearchCriteria(); }, [fetchSearchCriteria]);
    // When the page has mounted and once the search criteria are known:
    // * If there is a date in the url, update state to this date
    // * If there is no date in the url, but there is publication data, update state to the correct search criteria
    useEffect(function () {
        var _a, _b;
        if (currentDate) {
            dispatch(updateSelectedDate(currentDate));
        }
        else if ((monthlyParams === null || monthlyParams === void 0 ? void 0 : monthlyParams.publicationId) && monthlyParams.publicationSlug) {
            var publicationTitle = ((_b = (_a = searchCriteria.data) === null || _a === void 0 ? void 0 : _a.find(function (criteria) { return criteria.name === monthlyParams.publicationSlug; })) === null || _b === void 0 ? void 0 : _b.title) || '';
            dispatch(updateSelectedCriteria({ id: monthlyParams.publicationId, name: monthlyParams.publicationSlug, title: publicationTitle }));
        }
    }, [searchCriteria]);
    // Set search query to null whenever selectedCriteria changes to null (i.e. whenever user resets selected criteria in header or main page)
    useEffect(function () {
        if (!selectedCriteria) {
            setSearchQuery('');
        }
        else {
            setSearchQuery(selectedCriteria.title);
        }
    }, [selectedCriteria]);
    // Show list if there are available criteria, except when the user just selected a criterium
    useEffect(function () {
        var _a;
        setShowCriteriaList(availableCriteria.length > 0
            && searchQuery.toLowerCase() !== ((_a = selectedCriteria === null || selectedCriteria === void 0 ? void 0 : selectedCriteria.title) === null || _a === void 0 ? void 0 : _a.toLowerCase()));
    }, [availableCriteria]);
    // When calendar changes:
    // * Update date state
    // * Update selected criteria to null, since we return to the daily view
    // * Update url
    var onCalendarChange = useCallback(function (newDate) {
        setSelectedDate(newDate);
        dispatch(updateSelectedDate(dateToUrlDate(newDate)));
        dispatch(updateSelectedCriteria(null));
        if (!isApp)
            history.push(routesPaths.newspaperPageBase + "/date/" + dateToUrlDate(newDate));
        else
            history.push(routesPaths.newspaperPageBase + "/date/" + dateToUrlDate(newDate) + "?sport24app=true");
    }, [setSelectedDate, isApp, history]);
    // When a new search criteria is selected:
    // * Update search criteria state state
    // * Don't show criteria list anymore
    // * Update url
    var onCriteriaChange = useCallback(function (nextCriteria) {
        dispatch(updateSelectedCriteria(nextCriteria));
        setShowCriteriaList(false);
        if (!isApp)
            history.push(routesPaths.newspaperPageBase + "/" + nextCriteria.name + "/" + nextCriteria.id);
        else
            history.push(routesPaths.newspaperPageBase + "/" + nextCriteria.name + "/" + nextCriteria.id + "?sport24app=true");
    }, [history, dispatch, updateSelectedCriteria, setSearchQuery, isApp, setShowCriteriaList, selectedDate]);
    // Resets search criteria and returns to main page (remembering selected date)
    var reset = useCallback(function () {
        resetSearchCriteria();
        setSearchQuery('');
        if (!isApp)
            history.push(routesPaths.newspaperPageBase + "/date/" + dateToUrlDate(selectedDate));
        else
            history.push(routesPaths.newspaperPageBase + "/date/" + dateToUrlDate(selectedDate) + "?sport24app=true");
    }, [history, resetSearchCriteria, isApp, routesPaths, selectedDate]);
    var searchFieldWidthProp = { '--search-field-width': showBackBtn ? '281' : '341' };
    return (React.createElement("header", __assign({ className: cc(['tfm-header', className]) }, rest, { style: searchFieldWidthProp }),
        React.createElement("div", { className: "tfm-header-content" },
            !isApp && React.createElement("h1", { onClick: function () { if (!selectedCriteria && detailParams)
                    reset(); } }, "\u03A0\u03C1\u03C9\u03C4\u03BF\u03C3\u03B5\u03BB\u03B9\u03B4\u03B1"),
            React.createElement("div", { className: "tfm-header-content-controls" },
                React.createElement("div", { className: "tfm-header-content-controls-inner" },
                    !!selectedCriteria && (React.createElement(IconButton, { className: "tfm-header-content-back", "aria-label": "\u03B5\u03C0\u03B9\u03C3\u03C4\u03C1\u03AD\u03C8\u03C4\u03B5 \u03C3\u03C4\u03BF \u03C0\u03C1\u03BF\u03B7\u03B3\u03BF\u03CD\u03BC\u03B5\u03BD\u03BF \u03B1\u03C0\u03BF\u03C4\u03AD\u03BB\u03B5\u03C3\u03BC\u03B1", onClick: reset },
                        React.createElement(ArrowBackIcon, null))),
                    React.createElement(SearchField, { className: "tfm-header-content-search", onIconClick: reset, onChange: setSearchQuery, value: searchQuery, placeholder: "\u0391\u03BD\u03B1\u03B6\u03AE\u03C4\u03B7\u03C3\u03B7 \u0395\u03C6\u03B7\u03BC\u03B5\u03C1\u03AF\u03B4\u03B1\u03C2" }),
                    React.createElement(Calendar, { onDateChange: onCalendarChange, date: selectedDate, initialVisibleMonth: selectedDate ? function () { return selectedDate; } : null })),
                React.createElement(FilterButtons, { id: "articleFilterToggleHeader", className: "tfm-header-content-filter", showToggle: !selectedCriteria && !detailParams, onRoundBtnClicked: reset, onSwitch: onSelectedNewsGroupChange, currentSwitchValue: selectedNewsGroup }))),
        React.createElement(CriteriaList, { listRef: list, className: "tfm-header-criteria-list", criteria: availableCriteria, onChange: onCriteriaChange, visible: showCriteriaList })));
};
export default memo(Header);
